import { T } from '@agos/shared'
import { Lock } from 'lucide-react'
import type { ReactNode } from 'react'

import { NAV_SECTIONS } from '../generated/nav-tree'
import type { RouteGuard } from '../module-contract'
import { composedRoutes } from '../module-registry'
import { accessRefusal, useAccessContext } from './access'
import { AGOS_NAV_SECTIONS } from './agos-nav'
import { refusalDetail, refusalTitle, rightsNamedBy } from './route-refusal'

/**
 * The route's half of the gate — the half legacy never had, and the half this port had declared without
 * enforcing.
 *
 * `ModuleRoute.guard` describes what a route requires, and until now nothing read it on the routing side:
 * `canAccess` was wired to the sidebar only, so every ported page was reachable by URL by anyone signed in.
 * The server refuses the data, so no employee could *read* anything they should not — but the guards were
 * decorative, and every page spec claiming "the route admits whoever the server lets read" was describing
 * an intention rather than the behaviour. This closes that.
 *
 * **What a refusal shows.** Legacy renders the 404 page: a page that exists, that you are simply not
 * allowed to open, reported as one that does not exist. That is what makes FB-001 so hard to diagnose from
 * the outside — the sidebar offers *Všeobecné nastavenia*, the click gives *page not found*, and nothing
 * connects the two. Here the refusal says which gate closed and what it wanted, so the answer to *why can I
 * see the link but not the page* is on the screen instead of in the router source.
 *
 * **What it does not do** is invent gates. A route with no `guard` stays open — `/dashboard`, `/kitchen`
 * and `/lunch-menu` are unguarded in legacy too (FB-002), and closing them would lock cooks out of the
 * board on cutover day.
 */

/**
 * The rights the app's own guards name, read once - what a permission refusal titles a right against (§9.759).
 *
 * The routes, the sidebar's sections and their entries: static arrays, so this cannot change between renders. The
 * sentences themselves are in `route-refusal.ts`, apart from this markup.
 */
const KNOWN_RIGHTS = rightsNamedBy([
  ...composedRoutes().map((route) => route.guard),
  ...[...NAV_SECTIONS, ...AGOS_NAV_SECTIONS].flatMap((section) => [
    'guard' in section ? section.guard : undefined,
    ...section.items.map((item) => item.guard),
  ]),
])

export function RouteGate({ guard, children }: { guard?: RouteGuard; children: ReactNode }) {
  const access = useAccessContext()
  const refusal = accessRefusal(guard, access)

  if (refusal === null) return <>{children}</>

  return (
    <div
      className="max-w-xl mx-auto mt-10 rounded-2xl border border-border bg-card px-6 py-8 text-center"
      data-route-refused={refusal.gate}
    >
      <Lock className="w-8 h-8 text-muted-foreground mx-auto" />
      <p className="mt-4 text-base font-bold text-foreground">{refusalTitle(refusal)}</p>
      <p className="mt-2 text-sm text-muted-foreground">{refusalDetail(refusal, access.systemRole, KNOWN_RIGHTS)}</p>
      <p className="mt-4 text-[11px] text-muted-foreground">
        {/* The distinction legacy's 404 destroyed. */}
        <T>Stránka existuje - len ju nemáte otvorenú vy.</T>
      </p>
    </div>
  )
}
